import React, { useState } from 'react';
import clsx from 'clsx';
import MuiTooltip, { TooltipProps as MuiTooltipProps } from '@material-ui/core/Tooltip';
import { ReactComponent as TooltipIcon } from 'common-assets/assets/icons/alert-circle.svg';
import { ReactComponent as TooltipIconFull } from 'common-assets/assets/icons/alert-circle-full.svg';
import { ReactComponent as CloseIcon } from 'common-assets/assets/icons/close.svg';
import { useTranslation } from 'react-i18next';
import { useUniqueWcagValues } from 'common-react';
import { useIsMobile } from '../utils/useIsMobile';
import { useAutoFocus } from '../utils/useAutoFocus';
import { Dialog } from '../Dialog/Dialog';
import { Card } from '../Card/Card';
import { VisuallyHidden } from '../VisuallyHidden/VisuallyHidden';
import { Typography } from '../Typography/Typography';

import useStyles from './Tooltip.style';

export interface TooltipProps {
  type?: 'info' | 'info-orange' | 'full' | 'full-orange';
  children?: React.ReactElement;
  title: string | React.ReactNode;
  preTitle?: string;
  placement?: MuiTooltipProps['placement'];
  arrow?: boolean;
  open?: boolean;
  className?: string;
  iconClassName?: string;
  customZIndex?: number;
  id?: string;
  error?: string | boolean;
  customIcon?: React.ReactElement;
  testId?: string;
}


const Tooltip: React.FC<TooltipProps> = ({
  type = 'info',
  title,
  preTitle,
  arrow = true,
  children,
  className,
  placement = 'top',
  open,
  iconClassName,
  customZIndex,
  id,
  error,
  customIcon,
  testId,
  ...otherProps
}) => {
  const c = useStyles({ customZIndex });
  const [t] = useTranslation('components');
  const isMobile = useIsMobile();
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const autoFocusRef = useAutoFocus<HTMLButtonElement>();
  const { getUniqueValue } = useUniqueWcagValues();
  const tooltipId = id || getUniqueValue('tooltip');


  const tooltipIconClasses = clsx(
    iconClassName,
    c.tooltipIcon,
    error && c.errorIcon,
    type === 'info-orange' && c.infoOrangeIcon,
    type === 'full' && c.fullIcon,
    type === 'full-orange' && c.fullOrangeIcon,
  );

  const isFullType = type === 'full' || type === 'full-orange';
  const icon = isFullType
    ? <TooltipIconFull className={tooltipIconClasses} data-testid="tooltip-icon" />
    : children || customIcon || <TooltipIcon className={tooltipIconClasses} data-testid="tooltip-icon" />;

  const tooltipContent = (
    <div className={c.tooltipWrapper}>
      {preTitle && <Typography variant="body2" className={c.tooltipPreTitle} fontWeight="bold">{preTitle}</Typography>}
      <Typography variant="body2" className={c.tooltipTitle}>{title}</Typography>
    </div>
  );

  return (
    <span
      onMouseEnter={!isMobile ? () => setIsDialogOpen(true) : undefined}
      onMouseLeave={!isMobile ? () => setIsDialogOpen(false) : undefined}
      className={className}
      data-testid={testId}
    >
      <MuiTooltip
        onClick={() => setIsDialogOpen(true)}
        onClose={!isMobile ? () => setIsDialogOpen(false) : undefined}
        enterTouchDelay={10}
        leaveTouchDelay={10000}
        classes={{
          popper: c.root,
          tooltip: c.tooltip,
          arrow: c.arrow
        }}
        PopperProps={{
          'aria-live': 'assertive'
        }}
        placement={placement}
        arrow={arrow}
        title={tooltipContent}
        id={tooltipId}
        open={(!isMobile && open) || (!isMobile && isDialogOpen)}
        {...otherProps}>
        <button
          className={c.button}
          onClick={() => setIsDialogOpen(true)}
          aria-describedby={(isDialogOpen || open) ? tooltipId : undefined}
          type="button"
          data-testid="tooltip-trigger"
        >
          <VisuallyHidden>
            {isDialogOpen ? t('tooltip.close-tooltip') : t('tooltip.open-tooltip')}
          </VisuallyHidden>
          {icon}
        </button>
      </MuiTooltip>
      <Dialog
        closeHandler={() => setIsDialogOpen(false)}
        open={isMobile && isDialogOpen}
        className={c.dialog}
        paperInnerClassName={c.dialogPaperInner}
        contentWrapperClassName={c.dialogContentWrapper}
        showCloseIcon={false}
        dialogContentProps={{
          id: tooltipId,
          role: 'tooltip',
          'aria-live': 'assertive'
        }}
      >
        <button
          onClick={() => setIsDialogOpen(false)}
          className={c.closeButton}
          ref={autoFocusRef}
          type="button"
          autoFocus={true}
        >
          <CloseIcon className={c.closeIcon} aria-hidden="true" focusable="false"/>
        </button>
        <Card className={c.dialogCard}>
          {title}
        </Card>
      </Dialog>
    </span>
  );
};

export { Tooltip };
